/**
 * Helper for rendering raw application log lines in the UI.
 *
 * It turns a raw grep line (which may contain ANSI color escape sequences produced
 * by the backend log formatter) into safe HTML that:
 *   1. keeps the original Linux terminal colors, and
 *   2. turns known identifiers (process id, session id, request id) into clickable
 *      links that let the user re-filter the log by that identifier.
 *
 * Adding a new clickable identifier later is a one liner: just add an entry to
 * TOKEN_LINKS below. The component wires the click handling generically, so no
 * other change is needed to support a new link type.
 */

const ESC = '\u001b';

// Maps the ANSI SGR color codes used by the backend log formatter to CSS colors.
const ANSI_COLORS = {
    31: '#c0392b', // red - error
    33: '#b58900', // yellow - warning
    32: '#27ae60', // green - info
    35: '#8e44ad'  // purple - trace
};

// Clickable identifiers. Each token is matched on the raw line and, when clicked,
// replaces the whole search filter with a single include keyword (see the component).
// The surrounding spaces are part of the keyword so " PID14 " does not match " PID141 ".
const TOKEN_LINKS = [
    { type: 'pid', label: 'Process', pattern: /(PID\d+)/g },
    { type: 'session', label: 'Session', pattern: /(SES\S+)/g },
    { type: 'request', label: 'Request', pattern: /(REQ[0-9a-fA-F]+)/g },
    { type: 'transaction', label: 'Transaction', pattern: /(TX[0-9a-fA-F]+)/g },
    { type: 'email', label: 'Email', pattern: /([A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})/g }
];

// Keywords that are always rendered in bold in the log output. The INCOMING/OUTGOING
// API request markers are included so the boundaries of each request stand out.
// Add more keywords here to highlight them in bold.
const BOLD_KEYWORDS = [
    'INCOMING API request START',
    'INCOMING API request END',
    'OUTGOING API request START',
    'OUTGOING API request END',
    'DB TRANSACTION BEGIN',
    'DB TRANSACTION COMMIT',
    'DB TRANSACTION ROLLBACK'
];

// Keywords that are always rendered in italic in the log output.
// Add more keywords here to highlight them in italic.
const ITALIC_KEYWORDS = [];

export default class {
    getTokenLinks () {
        return TOKEN_LINKS;
    }

    /**
     * Builds the search keyword for a token, padded with spaces so it matches the
     * exact identifier and nothing longer.
     */
    buildTokenKeyword (tokenValue) {
        return ` ${tokenValue} `;
    }

    /**
     * Renders a raw log line as safe HTML: ANSI colors first, then clickable tokens,
     * then keyword highlighting (bold and italic).
     */
    toHtml (rawLine) {
        const escaped = this.escapeHtml(rawLine || '');
        const colored = this.ansiToHtml(escaped);
        const linked = this.linkifyTokens(colored);
        return this.highlightKeywords(linked);
    }

    escapeHtml (text) {
        return text
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }

    /**
     * Converts ANSI SGR sequences (ESC[<codes>m) into balanced <span> tags.
     */
    ansiToHtml (text) {
        let openSpans = 0;
        const pattern = new RegExp(`${ESC}\\[([0-9;]*)m`, 'g');

        let result = text.replace(pattern, (match, codes) => {
            const parts = codes.split(';').filter(part => part !== '');
            const color = parts.map(part => ANSI_COLORS[part]).find(value => value);
            const isReset = !color && (parts.length === 0 || parts.indexOf('0') !== -1);

            if (color) {
                let prefix = '';
                if (openSpans > 0) {
                    prefix = '</span>';
                    openSpans--;
                }
                openSpans++;
                return `${prefix}<span style="color:${color}">`;
            }

            if (isReset && openSpans > 0) {
                openSpans--;
                return '</span>';
            }

            return '';
        });

        while (openSpans > 0) {
            result += '</span>';
            openSpans--;
        }

        return result;
    }

    /**
     * Wraps known identifiers in anchor tags carrying the token type and value in data
     * attributes. The component listens for clicks on these anchors.
     */
    linkifyTokens (html) {
        let result = html;
        TOKEN_LINKS.forEach(link => {
            const pattern = new RegExp(link.pattern.source, link.pattern.flags);
            result = result.replace(pattern, (match) => {
                return `<a href="" class="application-log-token" data-token-type="${link.type}" data-token-value="${match}">${match}</a>`;
            });
        });
        return result;
    }

    /**
     * Wraps the configured keywords in <b> and <i> tags so they stand out in the output.
     */
    highlightKeywords (html) {
        let result = html;
        BOLD_KEYWORDS.forEach(keyword => {
            result = this.wrapKeyword(result, keyword, 'b');
        });
        ITALIC_KEYWORDS.forEach(keyword => {
            result = this.wrapKeyword(result, keyword, 'i');
        });
        return result;
    }

    /**
     * Wraps every occurrence of a keyword in the given tag. The keyword is matched against
     * the already HTML-escaped line, so it is escaped the same way before building the regex.
     */
    wrapKeyword (html, keyword, tag) {
        const escapedKeyword = this.escapeHtml(keyword).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const pattern = new RegExp(escapedKeyword, 'g');
        return html.replace(pattern, (match) => `<${tag}>${match}</${tag}>`);
    }
}



// WEBPACK FOOTER //
// ./src/app/services/applicationLog/index.js